/**
 * FAQ API Service
 * Handles all FAQ-related API calls for the web frontend
 * Includes public endpoints for reading and interacting with FAQs
 */

import api, { ApiResponse } from './index'
import {
    FAQ,
    FAQsResponse,
    FAQDetailResponse,
    HelpfulResponse,
    FeaturedFAQsResponse,
    SearchFAQsResponse,
    FAQFilters,
    FAQCategoriesResponse
} from '@/types/faq'

export const faqsApi = {
    /**
     * Get all active FAQ categories for filtering
     * @returns Promise containing list of active categories
     */
    getCategories: (): Promise<ApiResponse<FAQCategoriesResponse>> =>
        api.get('/faqs/categories/active'),

    /**
     * Get all published FAQs with pagination and optional filtering
     * @param params - Filter parameters (page, limit, category, search, sortBy)
     * @returns Promise containing paginated FAQ list
     */
    getFAQs: (params: FAQFilters = {}): Promise<ApiResponse<FAQsResponse>> =>
        api.get('/faqs', { params }),

    /**
     * Get FAQs filtered by specific category
     * @param category - Category to filter by
     * @param params - Additional filter parameters
     * @returns Promise containing FAQs in the specified category
     */
    getFAQsByCategory: (category: string, params?: Omit<FAQFilters, 'category'>): Promise<ApiResponse<FAQsResponse>> =>
        api.get(`/faqs/category/${category}`, { params }),

    /**
     * Get featured FAQs (typically shown on homepage or sidebar)
     * @param limit - Maximum number of featured FAQs to return (default: 5)
     * @returns Promise containing featured FAQs
     */
    getFeaturedFAQs: (limit: number = 5): Promise<ApiResponse<FeaturedFAQsResponse>> =>
        api.get('/faqs/featured', { params: { limit } }),

    /**
     * Search FAQs by query string
     * Searches across both question and answer text
     * @param query - Search query string
     * @param params - Additional filter parameters
     * @returns Promise containing search results
     */
    searchFAQs: (query: string, params?: Omit<FAQFilters, 'search'>): Promise<ApiResponse<SearchFAQsResponse>> =>
        api.get('/faqs/search', { params: { query, ...params } }),

    /**
     * Get single FAQ by ID
     * @param id - FAQ ID
     * @returns Promise containing the FAQ details
     */
    getFAQById: (id: string): Promise<ApiResponse<FAQDetailResponse>> =>
        api.get(`/faqs/${id}`),

    /**
     * Track a view for an FAQ
     * Increments the view counter for the FAQ
     * @param id - FAQ ID
     * @returns Promise with confirmation and updated view count
     */
    trackFAQView: (id: string): Promise<ApiResponse<{ faq: FAQ }>> =>
        api.post(`/faqs/${id}/view`, {}),

    /**
     * Mark an FAQ as helpful by the current user
     * Increments the helpful counter for the FAQ
     * @param id - FAQ ID
     * @returns Promise with confirmation and updated helpful count
     */
    markFAQHelpful: (id: string): Promise<ApiResponse<HelpfulResponse>> =>
        api.post(`/faqs/${id}/helpful`, {})
}